import { createQuery } from "react-query-kit";
import { keepPreviousData, useQueries } from "@tanstack/react-query";
import { api } from "../../api";
import {
  GetPackageAdsPreSearchQuery,
  SearchPackageAdsRequest,
  GetPublicPackageAdsResponse,
  GetPublicPackageAdsPreSearchResponse,
  GetPublicPackageAdDetailResponse,
} from "../../contracts/package-ads";
import { LanguageCode } from "../../contracts/language-settings";
import { GuestDetails } from "../../contracts/reservation";

export const fetchPackages = async (
  params: SearchPackageAdsRequest & { siteId: string; filter: string },
) => {
  const { languageCode, arrivalDate, departureDate, guests, siteId, filter } =
    params;
  const body = {
    languageCode,
    arrivalDate,
    departureDate,
    guests: {
      adults: guests.adults,
      teenagers: guests.teenagers,
      children: guests.children?.map((child) => ({
        age: child.age,
        ageType: child.ageType,
        bedCapacityType: child.bedCapacityType,
      })),
      infants: guests.infants?.map((infant) => ({
        age: infant.age,
        ageType: infant.ageType,
        bedCapacityType: infant.bedCapacityType,
      })),
    },
  };

  const query = new URLSearchParams({
    ...(languageCode ? { languageCode } : {}),
    filter: filter ?? "none",
  });

  return api.post<GetPublicPackageAdsResponse>(
    `/api/public/package-ads/${siteId}?${query.toString()}`,
    body,
  );
};

// Bump when the PublicPackageAdListItem / PublicPackageAdDetailed shape changes
// in a way that old cached responses can't satisfy — old client builds cache the
// previous shape under the previous key and would serve stale data indefinitely
// otherwise.
const PACKAGE_ADS_SCHEMA_REV = "v1";

// POST /api/public/package-ads/{siteId}
export const useGetPackages = createQuery<
  GetPublicPackageAdsResponse,
  SearchPackageAdsRequest & { siteId: string; filter: string }
>({
  queryKey: ["useGetPackages", PACKAGE_ADS_SCHEMA_REV],
  fetcher: fetchPackages,
  enabled: false,
});

// GET /api/public/package-ads/{siteId}/pre-search
export const useGetPackagesPreSearch = createQuery<
  GetPublicPackageAdsPreSearchResponse,
  GetPackageAdsPreSearchQuery & { siteId: string; filter?: string }
>({
  queryKey: ["useGetPackagesPreSearch", PACKAGE_ADS_SCHEMA_REV],
  fetcher: async ({ siteId, languageCode, filter, packageAdId }) => {
    return api.get<GetPublicPackageAdsPreSearchResponse>(
      `/api/public/package-ads/${siteId}/pre-search`,
      {
        languageCode,
        filter,
        packageAdId,
      },
    );
  },
});

interface UnitPackagesQueryInput {
  unitId: string;
  variables: SearchPackageAdsRequest & { languageCode: LanguageCode };
}

interface UseGetPackagesByUnitsParams {
  queryInputs: UnitPackagesQueryInput[];
  siteId: string;
  filter: string;
  searchToken: number;
  enabled: boolean;
  refetchIntervalMs?: number;
  /**
   * Keep the previous key's data visible while a bumped `searchToken` makes
   * a new query key, instead of that new key's fresh pending/`undefined` —
   * a caller that forces a refetch on an unchanged search (v2's Search
   * button) needs this so it isn't skeletoning cards already on screen.
   * Opt-in: existing callers keep the plain new-key-means-fresh-pending
   * behavior by leaving this unset.
   */
  keepPreviousDataOnKeyChange?: boolean;
}

export const useGetPackagesByUnits = ({
  queryInputs,
  siteId,
  filter,
  searchToken,
  enabled,
  refetchIntervalMs,
  keepPreviousDataOnKeyChange,
}: UseGetPackagesByUnitsParams) => {
  return useQueries({
    queries: queryInputs.map(({ unitId, variables }) => {
      const queryVariables = {
        ...variables,
        siteId,
        filter,
      };
      const options = useGetPackages.getOptions(queryVariables);
      return {
        ...options,
        queryKey: [...useGetPackages.getKey(queryVariables), unitId, searchToken],
        enabled,
        staleTime: 10_000,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        refetchInterval: refetchIntervalMs,
        placeholderData: keepPreviousDataOnKeyChange ? keepPreviousData : undefined,
      };
    }),
  });
};

// GET /api/public/package-ads/{siteId}/{packageAdId}/detail
// Dates + guests are OPTIONAL: the query fires as soon as the dialog opens
// (siteId + packageAdId present) so the static content (images, description,
// included products, bound categories) renders immediately. Pricing +
// per-category availability come back null until a complete range is picked,
// at which point the dates enter the query variables (and thus the query key),
// triggering a refetch of the priced detail.
export const useGetPackageDetail = createQuery<
  GetPublicPackageAdDetailResponse,
  { siteId: string; packageAdId: string } & Partial<
    Pick<SearchPackageAdsRequest, "arrivalDate" | "departureDate" | "guests">
  > & { languageCode?: LanguageCode; preview?: boolean }
>({
  queryKey: ["useGetPackageDetail", PACKAGE_ADS_SCHEMA_REV],
  fetcher: async ({
    siteId,
    packageAdId,
    arrivalDate,
    departureDate,
    guests,
    languageCode,
    preview,
  }) => {
    // Dates are all-or-nothing (the backend rejects a half-range); only send
    // them when both are present. Guests ride along only when pricing.
    const hasRange = !!arrivalDate && !!departureDate;
    return api.get<GetPublicPackageAdDetailResponse>(
      `/api/public/package-ads/${siteId}/${packageAdId}/detail`,
      {
        ...(hasRange ? { arrivalDate, departureDate } : {}),
        // Express query strings can't reliably express the nested
        // children/infants arrays as repeated keys, so the backend accepts
        // `guests` as a JSON-encoded object (see the detail route's
        // alternatives schema). Match that here — passing the object directly
        // would serialize to "[object Object]". Only send guests alongside a
        // range (they only matter for pricing).
        ...(hasRange && guests ? { guests: JSON.stringify(guests) } : {}),
        // PMS preview of a hidden ad. Sent only when set, so a guest's query
        // key — and their cache entry — is exactly what it was before.
        ...(preview ? { preview: true } : {}),
        languageCode,
      },
    );
  },
});

/**
 * One priced detail per staged unit.
 *
 * A package is priced per occupancy, and each unit carries its own guests, so
 * one detail fetched with every guest summed into a single room prices the
 * wrong occupancy and then gets multiplied per unit. Querying per unit is what
 * makes the quote match the charge.
 *
 * Keyed by the unit's guest composition rather than its id: two units holding
 * the same guests cost the same, so they share one cache entry and one request.
 */
export const useGetPackageDetailsByUnits = ({
  siteId,
  packageAdId,
  arrivalDate,
  departureDate,
  languageCode,
  guestsByUnit,
  enabled,
  preview,
}: {
  siteId: string;
  packageAdId: string;
  arrivalDate?: string;
  departureDate?: string;
  languageCode?: LanguageCode;
  guestsByUnit: GuestDetails[];
  enabled?: boolean;
  /** PMS preview of a hidden ad — see usePackagePreview. */
  preview?: boolean;
}) =>
  useQueries({
    queries: guestsByUnit.map((guests) => {
      const variables = {
        siteId,
        packageAdId,
        arrivalDate,
        departureDate,
        guests,
        languageCode,
        preview,
      };
      return {
        ...useGetPackageDetail.getOptions(variables),
        enabled: enabled && !!siteId && !!packageAdId,
        placeholderData: keepPreviousData,
      };
    }),
  });
